import { ChevronRight } from '@material-ui/icons';
import { BaseClickable, Box, Link, palette, Stack, Text } from '@solvhealth/jigsaw-v1';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  MEMBER_TELEMED_VISIT_COST_CENTS,
  MEMBERSHIP_NAME,
  NON_MEMBER_TELEMED_VISIT_COST_CENTS,
  PREMIUM_TELEMED_SLA_MINUTES,
  PREMIUM_VISIT_NAME,
} from '~/components/Membership/constants';
import UpsellModal, { SOLV_PLUS_UPSELL_MODAL } from '~/components/Membership/Signup/UpsellModal';
import { useOnMembershipEntrypointClicked } from '~/components/Membership/utils';
import { VideoVisitMemberPrice } from '~/components/Membership/VideoVisitMemberPrice';
import { SOLV_PLUS_ENTRYPOINT_ACCOUNT_HOME } from '~/core/analytics/events';
import {
  getProfileFromAccountSummary,
  isUserProfileCoveredByMembership,
} from '~/core/util/account';
import { useAccount } from '~/hooks/account/useAccount';
import useToggleRuntimeModal from '~/hooks/runtime/useToggleRuntimeModal';
import { centsToCurrencyStr } from '~/core/util/currency';

type Props = {
  userProfileId: string;
};

const StyledBox = styled(Box).withConfig({ componentId: 'sc-hd6a3x-0' })`
  display: flex;
  flex-wrap: wrap;
`;

const SolvPremiumEntrypoint = ({ userProfileId }: Props) => {
  const { t } = useTranslation(['account']);
  const [, toggle] = useToggleRuntimeModal(SOLV_PLUS_UPSELL_MODAL);

  const userProfile = getProfileFromAccountSummary(useAccount(), userProfileId);
  const isUserProfileWithMembershipAccess = isUserProfileCoveredByMembership(
    useAccount(),
    userProfile
  );

  const { onClick } = useOnMembershipEntrypointClicked(SOLV_PLUS_ENTRYPOINT_ACCOUNT_HOME);

  return (
    <Stack borderRadius="base" boxShadow="xs" flexShrink={0} overflow="hidden">
      <BaseClickable onClick={() => onClick()}>
        <Stack alignItems="flex-start" p={2} space={2}>
          <Stack row>
            <Stack space={0.5}>
              <Text variant="h4">{t('account:solvPremiumEntrypoint.title')}</Text>
              <Text muted>
                {t('account:solvPremiumEntrypoint.subtitle', {
                  PREMIUM_TELEMED_SLA_MINUTES,
                  SOLV_NOW_NAME: PREMIUM_VISIT_NAME,
                })}
              </Text>
            </Stack>
            <img alt="open 24/7 icon" height={34} src="/images/account/open24.svg" width={74} />
          </Stack>
          <Stack
            alignItems="center"
            row
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Stack alignItems="center" row space={0.5}>
              <Text textColor="crunchberry">{t('account:solvPremiumEntrypoint.cta')}</Text>
              <ChevronRight htmlColor={palette.crunchberry.base} style={{ fontSize: '18px' }} />
            </Stack>
            <Stack alignItems="center" row space={1}>
              {isUserProfileWithMembershipAccess ? (
                <VideoVisitMemberPrice />
              ) : (
                <Text>{centsToCurrencyStr(NON_MEMBER_TELEMED_VISIT_COST_CENTS)}</Text>
              )}
            </Stack>
          </Stack>
        </Stack>
      </BaseClickable>
      {!isUserProfileWithMembershipAccess && (
        <StyledBox bg="teal-xlight" px={2} py={1.5}>
          <Text>
            {t('account:solvPremiumEntrypoint.solvMembershipUpsell', {
              TELEMED_VISIT_COST_STRING: centsToCurrencyStr(MEMBER_TELEMED_VISIT_COST_CENTS),
              MEMBERSHIP_NAME,
            })}
            &nbsp;
          </Text>
          <Link
            inline
            onClick={toggle}
            subtle
            sx={{ textDecoration: 'underline', textAlign: 'left' }}
          >
            {t('account:solvPremiumEntrypoint.linkCta')}
          </Link>
        </StyledBox>
      )}
      <UpsellModal canInitiateBooking />
    </Stack>
  );
};

export default SolvPremiumEntrypoint;
